import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { displayAddress } from '@/webapp/siteAddress';
import { EDIT_COST } from '@/billing/buildCostNote';
import type { AppDefinition } from '@/types/app';
import { useSession } from '../../state/useSession';
import { useBuildRun } from '../../state/buildRun';
import { useProjects } from '../../state/projects';
import { creditBalance, type CreditBalance } from '../../api/billing';
import { sendLiveLink } from '../../api/emailLink';
import { ApiError } from '../../api/client';
import { freeUntilDate, freeWeekRunning } from '../../state/trial';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';
import { Button } from '../../ui';
import FreeWeekOffer from './FreeWeekOffer';
import { cameBackLater, markSeen } from '../../state/liveSeen';

/**
 * /new/live/:id — "Your app is live" (V6, docs/superpowers/specs/2026-10-14-v6-design.md). Replaces
 * "their site for 15 s, then the offer" for an account on its free week: the released app is
 * published at once (state/buildRun.ts) and shown here, full screen, at its real address, with a bar
 * that says where it lives and until when it is free, and three things to do with it:
 *
 *   - Email me (the link): the address, emailed to the account (a quick win, no offer).
 *   - Share: the phone's share sheet, or the link copied.
 *   - Change (something): the project's chat, where the week's free changes are, counted under the row.
 *
 * The offer to keep it live (FreeWeekOffer) opens when they act on the app (Share, Change something),
 * from "Keep it live" in the bar, and on a later visit while the week runs. Never on a timer.
 *
 * The site is framed from its own origin (app.saddle-sites.com), which allows it only for the
 * builder's origin, so it runs with its own storage and can never read the builder's.
 */
export default function Live() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const fromBuild = useBuildRun((s) => s.liveApp);
  const [app, setApp] = useState<{ name: string; siteUrl: string; appId: string; until?: string } | null>(() =>
    fromBuild && fromBuild.projectId === id ? fromBuild : null,
  );
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  // Read before this visit marks it: whether they have been here before, on another visit.
  const [later] = useState(() => cameBackLater(id));
  useEffect(() => markSeen(id), [id]);
  useFunnelStep('live_viewed');

  // A later visit has no build in hand: the project says where its site is.
  useEffect(() => {
    if (app || !user?.id) return;
    let live = true;
    void Promise.resolve(useProjects.getState().loadProject(id, user.id))
      .catch(() => undefined)
      .then(() => {
        if (!live) return;
        const project = useProjects.getState().currentProject;
        const def = project?.id === id ? (project.definition as AppDefinition) : null;
        // No page to show: the editor, where Publish is.
        if (!def?.webUrl) navigate(`/project/${id}`, { replace: true });
        else setApp({ name: def.name || project!.name, siteUrl: def.webUrl, appId: def.id });
      });
    return () => {
      live = false;
    };
    // Once per project and person.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  useEffect(() => {
    let live = true;
    creditBalance().then((b) => live && setBalance(b), () => undefined);
    return () => {
      live = false;
    };
  }, []);

  const until = balance?.freeWeek?.until ?? app?.until;
  const subscribed = Boolean(balance && (balance.subscribed === true || balance.tier === 'paid' || balance.tier === 'unlimited'));
  const offerable = Boolean(until) && !subscribed;

  const [offerOpen, setOfferOpen] = useState(false);
  /** Where "Not now" goes: back to the site, or on to the editor for a change. */
  const afterOffer = useRef<'stay' | 'change'>('stay');
  // A later visit, while the week runs: the offer at once.
  const autoOffered = useRef(false);
  useEffect(() => {
    if (autoOffered.current || !later || !balance || !app) return;
    if (freeWeekRunning(balance)) {
      autoOffered.current = true;
      setOfferOpen(true);
    }
  }, [later, balance, app]);

  function offerThen(next: 'stay' | 'change'): void {
    afterOffer.current = next;
    if (offerable) setOfferOpen(true);
    else if (next === 'change') navigate(`/project/${id}`);
  }

  const [linkState, setLinkState] = useState<{ kind: 'idle' | 'sending' | 'sent' | 'copied' } | { kind: 'error'; message: string }>({ kind: 'idle' });
  async function sendLink(): Promise<void> {
    if (!app || linkState.kind === 'sending') return;
    setLinkState({ kind: 'sending' });
    try {
      await sendLiveLink(app.appId);
      funnelStep('link_sent');
      setLinkState({ kind: 'sent' });
    } catch (e) {
      setLinkState({
        kind: 'error',
        message: e instanceof ApiError && e.kind === 'rate_limited'
          ? 'We just sent it. Check your inbox, and try again in a few minutes.'
          : 'That didn’t send. Try again in a moment.',
      });
    }
  }

  async function share(): Promise<void> {
    if (!app) return;
    funnelStep('site_shared');
    const nav = navigator as Navigator & { share?: (d: { title?: string; url?: string }) => Promise<void> };
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: app.name, url: app.siteUrl });
      } catch {
        /* closed without sharing: the offer still follows the press */
      }
    } else {
      try {
        await navigator.clipboard.writeText(app.siteUrl);
        setLinkState({ kind: 'copied' });
      } catch {
        setLinkState({ kind: 'error', message: `Copy this link: ${app.siteUrl}` });
      }
    }
    offerThen('stay');
  }

  if (!app) {
    return (
      <div className="live-full">
        <p className="type-body live-loading" role="status">Opening your app…</p>
      </div>
    );
  }

  const date = until ? freeUntilDate(until, true) : '';
  const ended = until ? Date.parse(until) <= Date.now() : false;
  // The week's free changes (3 edits' worth of credits), said only while the week runs.
  const running = balance ? freeWeekRunning(balance) : false;
  const changesLeft = running && balance ? Math.floor((balance.gift?.remaining ?? balance.creditsRemaining ?? 0) / EDIT_COST) : 0;
  /*
   * Compact (V6 polish, 2026-10-14): the bar took a third of the phone. Where it stands on one line,
   * the short link on the next, three short buttons in one row, and Keep it live: at most a quarter
   * of a 390x844 screen. The heading is for screen readers; the line under it says the same.
   */
  return (
    <div className={offerOpen ? 'live-full live-full-offer' : 'live-full'}>
      <iframe
        className="live-frame"
        title={`${app.name}, live`}
        src={app.siteUrl}
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
      />
      {/* Put away while the offer's sheet is open, so nothing of it shows under the sheet. */}
      <section className="live-bar" aria-labelledby="live-title" hidden={offerOpen}>
        <h1 id="live-title" className="visually-hidden">Your {app.name} is live</h1>
        <div className="live-where">
          <p className="type-caption live-line">
            {offerable && date ? (ended ? `Paused since ${date}` : `Live · free until ${date}`) : 'Live'}
          </p>
          <a className="type-caption live-link" href={app.siteUrl} target="_blank" rel="noopener noreferrer">{displayAddress(app.siteUrl)}</a>
        </div>
        <div className="live-actions">
          <Button
            title={linkState.kind === 'sent' ? 'Sent' : 'Email me'}
            accessibilityLabel={linkState.kind === 'sent' ? 'Sent' : 'Email me the link'}
            variant="secondary"
            loading={linkState.kind === 'sending'}
            disabled={linkState.kind === 'sent'}
            onPress={() => void sendLink()}
          />
          <Button title="Share" variant="secondary" onPress={() => void share()} />
          <Button
            title="Change"
            accessibilityLabel="Change something"
            variant="secondary"
            onPress={() => {
              // Straight to the editor: the week comes with free changes (owner, 2026-10-14), and
              // making it theirs is what makes it worth keeping. The offer waits for Share, Keep it live
              // or a later visit.
              funnelStep('change_pressed');
              navigate(`/project/${id}`);
            }}
          />
        </div>
        {changesLeft > 0 && (
          <p className="type-caption live-changes">{changesLeft} free change{changesLeft === 1 ? '' : 's'} this week</p>
        )}
        <div className="live-status" aria-live="polite">
          {linkState.kind === 'sent' && <p className="type-caption">Sent to {user?.email}</p>}
          {linkState.kind === 'copied' && <p className="type-caption">Link copied</p>}
          {linkState.kind === 'error' && <p role="alert" className="type-caption field-error-text">{linkState.message}</p>}
        </div>
        {offerable && (
          <Button
            title="Keep it live"
            onPress={() => {
              funnelStep('keep_live_pressed');
              offerThen('stay');
            }}
          />
        )}
      </section>
      {offerOpen && until && (
        <FreeWeekOffer
          appName={app.name}
          until={until}
          appId={app.appId}
          ownerId={user?.id ?? ''}
          onClose={() => {
            setOfferOpen(false);
            if (afterOffer.current === 'change') navigate(`/project/${id}`);
          }}
        />
      )}
    </div>
  );
}
